<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 px-6 py-4">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div>
                                        <h1 class="text-2xl font-semibold text-gray-900 m-0">Cross Sell</h1>
                                        <p class="text-sm text-gray-500 mt-1 mb-0">Manage cross sell offerings</p>
                                </div>
                                <div class="flex items-center gap-2">
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium cursor-pointer transition-colors"
                                                :disabled="isLoading"
                                                @click="loadData"
                                        >
                                                <svg class="w-4 h-4" :class="{ 'animate-spin': isLoading }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                Refresh
                                        </button>
                                        <button
                                                type="button"
                                                class="inline-flex items-center gap-2 px-4 py-2.5 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer hover:bg-orange-6"
                                                @click="showCreateModal = true"
                                        >
                                                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                                                </svg>
                                                Create Cross Sell
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Main Content -->
                <main class="p-6">
                        <!-- Search and Filters Card -->
                        <div class="bg-white rounded-xl shadow-sm px-6 py-4 mb-4">
                                <div class="flex flex-wrap gap-4 items-center">
                                        <div class="flex-1 min-w-48">
                                                <div class="relative">
                                                        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                        </svg>
                                                        <input
                                                                v-model="searchQuery"
                                                                type="text"
                                                                placeholder="Search cross sells..."
                                                                class="w-full py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm outline-none focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                                @input="applyFilters"
                                                        >
                                                </div>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-700 font-medium">Published:</label>
                                                <select
                                                        v-model="publishedFilter"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-28 focus:border-orange-7"
                                                        @change="applyFilters"
                                                >
                                                        <option :value="null">All</option>
                                                        <option :value="true">Yes</option>
                                                        <option :value="false">No</option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-3">
                                                <label class="text-sm text-gray-700 font-medium">Language:</label>
                                                <select
                                                        v-model="selectedLanguage"
                                                        class="py-2 px-3 border border-gray-300 rounded-lg text-sm outline-none min-w-32 focus:border-orange-7"
                                                        @change="loadTranslations"
                                                >
                                                        <option v-for="lang in languages" :key="lang.id" :value="lang.id">
                                                                {{ lang.name }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div class="flex items-center gap-2">
                                                <span class="text-xs bg-orange-2 text-orange-7 px-3 py-1 rounded-full font-medium">
                                                        {{ filteredItems.length }} cross sells
                                                </span>
                                        </div>
                                </div>
                        </div>

                        <!-- Loading State -->
                        <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                                <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span class="ml-3 text-gray-500 text-sm">Loading cross sells...</span>
                        </div>

                        <!-- Table Card -->
                        <div v-else class="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div class="overflow-x-auto">
                                        <table class="w-full border-collapse">
                                                <thead>
                                                        <tr class="bg-gray-50">
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-20">ID</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Name</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Header</th>
                                                                <th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase border-b border-gray-200">Description</th>
                                                                <th class="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-24">Published</th>
                                                                <th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase border-b border-gray-200 w-32">Actions</th>
                                                        </tr>
                                                </thead>
                                                <tbody>
                                                        <tr v-if="paginatedItems.length === 0">
                                                                <td colspan="6" class="text-center py-12 px-4">
                                                                        <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                                                        </svg>
                                                                        <p class="text-gray-500 text-sm m-0">No cross sells found</p>
                                                                        <p class="text-gray-400 text-xs mt-1 mb-0">Create a new cross sell to get started</p>
                                                                </td>
                                                        </tr>
                                                        <tr
                                                                v-for="item in paginatedItems"
                                                                :key="item.id"
                                                                class="border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                                                                @click="editItem(item.id)"
                                                        >
                                                                <td class="py-3 px-4 text-sm text-gray-500 font-mono">{{ item.id }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-900 font-medium">{{ item.name }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-700">{{ item.header || '-' }}</td>
                                                                <td class="py-3 px-4 text-sm text-gray-500 max-w-80 overflow-hidden text-ellipsis whitespace-nowrap">{{ item.description || '-' }}</td>
                                                                <td class="py-3 px-4 text-center">
                                                                        <span
                                                                                v-if="item.published"
                                                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                                                        >
                                                                                Yes
                                                                        </span>
                                                                        <span
                                                                                v-else
                                                                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500"
                                                                        >
                                                                                No
                                                                        </span>
                                                                </td>
                                                                <td class="py-3 px-4 text-right">
                                                                        <div class="flex justify-end gap-2">
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-orange-7"
                                                                                        title="Edit"
                                                                                        @click.stop="editItem(item.id)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="p-1.5 text-gray-500 bg-transparent border-none cursor-pointer rounded-md hover:text-red-600"
                                                                                        title="Delete"
                                                                                        @click.stop="confirmDelete(item)"
                                                                                >
                                                                                        <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                                        </svg>
                                                                                </button>
                                                                        </div>
                                                                </td>
                                                        </tr>
                                                </tbody>
                                        </table>
                                </div>

                                <!-- Pagination -->
                                <div v-if="totalPages > 1" class="flex justify-between items-center px-6 py-4 border-t border-gray-200">
                                        <div class="text-sm text-gray-500">
                                                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredItems.length) }} of {{ filteredItems.length }} cross sells
                                        </div>
                                        <div class="flex gap-2">
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === 1"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage--"
                                                >
                                                        Previous
                                                </button>
                                                <button
                                                        v-for="page in visiblePages"
                                                        :key="page"
                                                        type="button"
                                                        class="py-2 px-3 border rounded-md text-sm cursor-pointer min-w-10"
                                                        :class="page === currentPage ? 'bg-orange-7 text-white border-orange-7' : 'bg-white text-gray-700 border-gray-300'"
                                                        @click="currentPage = page"
                                                >
                                                        {{ page }}
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="currentPage === totalPages"
                                                        class="py-2 px-3 border border-gray-300 bg-white rounded-md text-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                        @click="currentPage++"
                                                >
                                                        Next
                                                </button>
                                        </div>
                                </div>
                        </div>
                </main>

                <!-- Create Modal -->
                <div v-if="showCreateModal" class="fixed inset-0 z-50 flex items-center justify-center">
                        <div class="absolute inset-0 bg-black/50" @click="showCreateModal = false"></div>
                        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-md m-4">
                                <div class="p-6">
                                        <h3 class="text-lg font-semibold text-gray-900 m-0 mb-6">Create New Cross Sell</h3>
                                        <div class="mb-4">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                <input
                                                        v-model="newItemName"
                                                        type="text"
                                                        placeholder="Enter cross sell name"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
                                                >
                                        </div>
                                        <div class="mb-6">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Cross Sell ID (Offering)</label>
                                                <select
                                                        v-model="newItemCrossSellId"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border focus:border-orange-7"
                                                >
                                                        <option :value="null">Select an offering (optional)</option>
                                                        <option v-for="offering in offerings" :key="offering.id" :value="offering.id">
                                                                {{ offering.id }} | {{ offering.size }} - {{ offering.description }}
                                                        </option>
                                                </select>
                                        </div>
                                        <div class="flex justify-end gap-3">
                                                <button
                                                        type="button"
                                                        class="py-2.5 px-4 bg-white text-gray-700 border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50"
                                                        @click="showCreateModal = false"
                                                >
                                                        Cancel
                                                </button>
                                                <button
                                                        type="button"
                                                        :disabled="!newItemName.trim() || isCreating"
                                                        class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer disabled:opacity-50 hover:bg-orange-6"
                                                        @click="createItem"
                                                >
                                                        {{ isCreating ? 'Creating...' : 'Create' }}
                                                </button>
                                        </div>
                                </div>
                        </div>
                </div>
        </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import CrossSellService from 'services/CrossSellService';
import OfferingService from 'services/OfferingService';
import LanguageService from 'services/LanguageService';
import TranslationService from 'services/TranslationService';

export default defineComponent({
        name: 'CrossSellsPage',
        setup() {
                const router = useRouter();

                const items = ref([]);
                const offerings = ref([]);
                const languages = ref([]);
                const selectedLanguage = ref('');
                const translations = ref({});
                const searchQuery = ref('');
                const publishedFilter = ref(true);
                const currentPage = ref(1);
                const pageSize = 20;
                const isLoading = ref(true);
                const showCreateModal = ref(false);
                const newItemName = ref('');
                const newItemCrossSellId = ref(null);
                const isCreating = ref(false);

                const filteredItems = computed(() => {
                        let result = items.value;
                        
                        if (publishedFilter.value !== null) {
                                const filterValue = publishedFilter.value ? 1 : 0;
                                result = result.filter(item => item.published === filterValue || item.published === publishedFilter.value);
                        }
                        
                        if (searchQuery.value.trim()) {
                                const query = searchQuery.value.toLowerCase();
                                result = result.filter(item =>
                                        item.name?.toLowerCase().includes(query) ||
                                        item.header?.toLowerCase().includes(query) ||
                                        item.description?.toLowerCase().includes(query) ||
                                        String(item.id).includes(query)
                                );
                        }
                        
                        return result;
                });

                const totalPages = computed(() => Math.ceil(filteredItems.value.length / pageSize));

                const paginatedItems = computed(() => {
                        const start = (currentPage.value - 1) * pageSize;
                        return filteredItems.value.slice(start, start + pageSize);
                });

                const visiblePages = computed(() => {
                        const pages = [];
                        const maxVisible = 5;
                        let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
                        let end = Math.min(totalPages.value, start + maxVisible - 1);
                        if (end - start + 1 < maxVisible) {
                                start = Math.max(1, end - maxVisible + 1);
                        }
                        for (let i = start; i <= end; i++) {
                                pages.push(i);
                        }
                        return pages;
                });

                const applyFilters = () => {
                        currentPage.value = 1;
                };

                const loadData = async () => {
                        isLoading.value = true;
                        try {
                                const [crossSellsResponse, offeringsResponse, languagesResponse] = await Promise.all([
                                        CrossSellService.getAll(new URLSearchParams({ limit: '0' })),
                                        OfferingService.getAll(new URLSearchParams({ limit: '0', fields: 'id,size,description' })),
                                        LanguageService.getAll(new URLSearchParams({ limit: '0' })),
                                ]);
                                items.value = crossSellsResponse;
                                offerings.value = offeringsResponse;
                                languages.value = languagesResponse;

                                if (languages.value.length > 0) {
                                        selectedLanguage.value = languages.value[0].id;
                                        await loadTranslations();
                                }
                        } catch (error) {
                                console.error('Failed to load data:', error);
                                Swal.fire({
                                        title: 'Error',
                                        text: 'Failed to load data',
                                        icon: 'error',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const loadTranslations = async () => {
                        if (!selectedLanguage.value) return;

                        try {
                                const params = new URLSearchParams({
                                        filter: `languageid||$eq||${selectedLanguage.value}`,
                                        limit: '0',
                                });
                                const response = await TranslationService.getAll(params);
                                translations.value = {};
                                for (const translation of response) {
                                        translations.value[translation.key] = translation.value;
                                }
                                items.value = items.value.map(item => ({
                                        ...item,
                                        header: translations.value[`crosssell.${item.id}.header`] || item.header,
                                        description: translations.value[`crosssell.${item.id}.description`] || item.description,
                                }));
                        } catch (error) {
                                console.error('Failed to load translations:', error);
                        }
                };

                const editItem = (id) => {
                        router.push(`/crosssell/${id}`);
                };

                const confirmDelete = async (item) => {
                        const result = await Swal.fire({
                                title: 'Delete Cross Sell?',
                                text: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonColor: '#dc2626',
                                cancelButtonColor: '#6b7280',
                                confirmButtonText: 'Yes, delete it',
                                cancelButtonText: 'Cancel',
                        });

                        if (result.isConfirmed) {
                                try {
                                        await CrossSellService.delete(item.id);
                                        items.value = items.value.filter(i => i.id !== item.id);
                                        Swal.fire({
                                                toast: true,
                                                position: 'top-end',
                                                icon: 'success',
                                                title: 'Cross sell deleted successfully',
                                                showConfirmButton: false,
                                                timer: 3000,
                                        });
                                } catch (error) {
                                        Swal.fire({
                                                title: 'Error',
                                                text: error.message || 'Failed to delete cross sell',
                                                icon: 'error',
                                        });
                                }
                        }
                };

                const createItem = async () => {
                        if (!newItemName.value.trim()) return;

                        isCreating.value = true;
                        try {
                                const newCrossSell = await CrossSellService.create({
                                        name: newItemName.value.trim(),
                                        crosssellid: newItemCrossSellId.value,
                                });
                                showCreateModal.value = false;
                                newItemName.value = '';
                                newItemCrossSellId.value = null;
                                router.push(`/crosssell/${newCrossSell.id}`);
                        } catch (error) {
                                Swal.fire({
                                        title: 'Error',
                                        text: error.message || 'Failed to create cross sell',
                                        icon: 'error',
                                });
                        } finally {
                                isCreating.value = false;
                        }
                };

                onMounted(() => {
                        loadData();
                });

                return {
                        items,
                        offerings,
                        languages,
                        selectedLanguage,
                        searchQuery,
                        publishedFilter,
                        currentPage,
                        pageSize,
                        isLoading,
                        showCreateModal,
                        newItemName,
                        newItemCrossSellId,
                        isCreating,
                        filteredItems,
                        totalPages,
                        paginatedItems,
                        visiblePages,
                        applyFilters,
                        loadTranslations,
                        editItem,
                        confirmDelete,
                        createItem,
                };
        },
});
</script>
